import React, { useMemo } from 'react';
import { getSiteContext } from '../../../state/contexts/site';
import { useSelector } from 'react-redux';
import {
  subSnapshots,
  getNumericDataSet,
  findTagLookupBySite,
  findEquipmentBySite,
} from '../../../state/entities';
import DashboardPage from './DashboardPage';
import { useParamSelector, useSubscription } from '../../../helpers/hooks';
import { getTagsByAttributes, buildFlows } from './config';
import { SDate } from '../../../helpers/SettableDate';
import { RootState } from '../../../state';
import { AttrType } from '../../../models/site';
const FOUR_HOURS = 1000 * 60 * 60 * 4;

export const DashboardPageLoader: React.FC<{}> = () => {
  const site = useSelector(getSiteContext);
  const tags = useParamSelector(findTagLookupBySite)(site.id);
  const siteFetchTime = useMemo(() => {
    return new Date();
  }, []);

  const powerflowTags = useMemo(() => getTagsByAttributes(tags, AttrType.Current), [tags]);
  const historicalTags = useMemo(() => getTagsByAttributes(tags, AttrType.Historical), [tags]);
  const extraAttributesTags = useMemo(
    () => getTagsByAttributes(tags, AttrType.ExtraAttributes),
    [tags]
  );

  const tagIds = useMemo(
    () => [...powerflowTags, ...extraAttributesTags].map((t) => t.id),
    [powerflowTags, extraAttributesTags]
  );

  const pointDataStatus = useSubscription(true, subSnapshots, site.id, tagIds, FOUR_HOURS);
  const minus4Hours = new Date(SDate.now() - FOUR_HOURS);
  const now = new Date(SDate.now());
  const latestSnapshots = useParamSelector(getNumericDataSet)(tagIds, minus4Hours, now);

  const lastFetchTime = useMemo(() => {
    return Object.values(latestSnapshots || {}).reduce<Date | undefined>((latestTime, snapshot) => {
      const snapshotTime = snapshot.points[snapshot.points.length - 1]?.time;
      if (!snapshotTime) return latestTime;
      const currentDate = new Date(snapshotTime);
      if (!latestTime || currentDate > latestTime) {
        return currentDate;
      }
      return latestTime;
    }, undefined);
  }, [latestSnapshots]);

  const equipmentList = useSelector((rootState: RootState) =>
    findEquipmentBySite(rootState, site.id)
  );

  const flows = useMemo(
    () => buildFlows(latestSnapshots, historicalTags),
    [latestSnapshots, historicalTags]
  );

  return (
    <DashboardPage
      site={site}
      flows={flows}
      dataStatus={pointDataStatus}
      equipmentList={equipmentList}
      lastFetchTime={lastFetchTime}
      siteFetchTime={siteFetchTime}
    />
  );
};
